import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Scan, UploadCloud, FileText, Globe, Crown, ArrowRight, X, CheckCircle, Lightbulb } from 'lucide-react';

const STEPS = [
  {
    id: 'welcome',
    icon: <Scan className="w-10 h-10 text-blue-500" />,
    title: '¡Bienvenido a Veritas Scan!',
    desc: 'La herramienta más rápida para detectar si una imagen, texto o documento fue generado por inteligencia artificial.',
    tip: null,
  },
  {
    id: 'upload',
    icon: <UploadCloud className="w-10 h-10 text-purple-400" />,
    title: 'Sube cualquier contenido',
    desc: 'Arrastra una imagen, sube un PDF, pega texto directamente o introduce una URL. Soportamos JPG, PNG, PDF, DOCX, TXT y más.',
    tip: 'También puedes arrastrar archivos directamente desde tu escritorio.',
  },
  {
    id: 'result',
    icon: <CheckCircle className="w-10 h-10 text-emerald-400" />,
    title: 'Obtén el veredicto en segundos',
    desc: 'Recibirás un veredicto claro (Real o IA Generada), un porcentaje de confianza y una explicación detallada con los indicadores detectados.',
    tip: 'Cuanto más texto o detalle tenga el contenido, más preciso será el análisis.',
  },
  {
    id: 'pro',
    icon: <Crown className="w-10 h-10 text-yellow-400" />,
    title: 'Desbloquea todo con Pro',
    desc: 'Con el plan Pro tienes análisis ilimitados, historial sincronizado en la nube, exportación de informes PDF, análisis por lotes y acceso a la API.',
    tip: null,
    proHighlights: [
      'Análisis ilimitados',
      'Historial en la nube',
      'Exportar PDF',
      'API pública',
      'Análisis por lotes',
    ],
  },
];

export default function Onboarding({ onClose, onNavigatePrecios }) {
  const [step, setStep] = useState(0);
  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;

  useEffect(() => {
    localStorage.setItem('veritas_onboarding_seen', 'true');
  }, []);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25 }}
          className="relative w-full max-w-md bg-zinc-900 border border-zinc-700 rounded-2xl overflow-hidden shadow-2xl"
        >
          {/* Close button */}
          <button onClick={onClose} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors z-10">
            <X className="w-5 h-5" />
          </button>

          {/* Progress bar */}
          <div className="h-1 bg-zinc-800">
            <motion.div
              className="h-full bg-blue-500"
              initial={{ width: 0 }}
              animate={{ width: `${((step + 1) / STEPS.length) * 100}%` }}
              transition={{ duration: 0.4 }}
            />
          </div>

          {/* Content */}
          <div className="p-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={step}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.25 }}
                className="space-y-5"
              >
                {/* Icon */}
                <div className="flex justify-center">
                  <div className="w-20 h-20 rounded-2xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                    {current.icon}
                  </div>
                </div>

                {/* Text */}
                <div className="text-center space-y-2">
                  <h2 className="font-heading text-2xl font-black">{current.title}</h2>
                  <p className="text-zinc-400 text-sm leading-relaxed">{current.desc}</p>
                </div>

                {/* Tip */}
                {current.tip && (
                  <div className="bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 text-zinc-400 text-xs leading-relaxed flex items-start gap-2">
                    <Lightbulb className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0 mt-0.5" />
                    {current.tip}
                  </div>
                )}

                {/* Pro highlights */}
                {current.proHighlights && (
                  <div className="grid grid-cols-1 gap-2">
                    {current.proHighlights.map((h, i) => (
                      <div key={i} className="flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2">
                        <CheckCircle className="w-4 h-4 text-yellow-400 flex-shrink-0" />
                        <span className="text-zinc-200 text-sm">{h}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Step indicator */}
                <div className="flex justify-center gap-2">
                  {STEPS.map((_, i) => (
                    <button key={i} onClick={() => setStep(i)}
                      className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-blue-500' : 'w-1.5 bg-zinc-700'}`}
                    />
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Actions */}
            <div className="mt-6 flex gap-3">
              {step > 0 && (
                <button onClick={() => setStep(s => s - 1)}
                  className="flex-1 py-3 border border-zinc-700 hover:border-zinc-500 text-zinc-400 font-mono text-sm uppercase tracking-wider rounded-xl transition-all">
                  Atrás
                </button>
              )}
              {isLast ? (
                <div className="flex-1 flex flex-col gap-2">
                  <button onClick={() => { onNavigatePrecios?.(); onClose(); }}
                    className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-mono text-sm uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2">
                    Ver planes <ArrowRight className="w-4 h-4" />
                  </button>
                  <button onClick={onClose}
                    className="w-full py-2 text-zinc-500 hover:text-zinc-300 font-mono text-xs transition-colors">
                    Continuar con el plan gratuito
                  </button>
                </div>
              ) : (
                <button onClick={() => setStep(s => s + 1)}
                  className="flex-1 py-3 bg-blue-600 hover:bg-blue-500 text-white font-mono text-sm uppercase tracking-wider rounded-xl transition-all flex items-center justify-center gap-2">
                  Siguiente <ArrowRight className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
